import { useTriggerLoad } from "@redotech/react-util/load";
import { useScrolled } from "@redotech/react-util/scroll";
import { ReturnAppSettings } from "@redotech/redo-customer-portal-shared/settings";
import { ReturnProduct } from "@redotech/redo-model/draft-return/draft-return-items";
import SearchIcon from "@redotech/redo-web/arbiter-icon/search-sm_filled.svg";
import { CurrencyContext } from "@redotech/redo-web/currency";
import * as gridCss from "@redotech/redo-web/grid.module.css";
import XIcon from "@redotech/redo-web/icon-old/x.svg";
import { LoadingRedoAnimation } from "@redotech/redo-web/loading-redo-animation";
import { SpinnerStyle } from "@redotech/redo-web/spinner";
import { TextInput } from "@redotech/redo-web/text-input";
import {
  ProductInfo,
  ShopifyStorefrontClient,
  getCollectionProductsPaginated,
  productInfoToReturnProduct,
  searchProducts as searchStorefrontProducts,
} from "@redotech/shopify-storefront";
import * as classNames from "classnames";
import { memo, useContext, useMemo, useState } from "react";
import { useDebounce } from "usehooks-ts";
import { Footer } from "../create-return/components/footer";
import * as footerCss from "../create-return/components/footer.module.css";
import * as returnStyles from "../create-return/components/return.module.css";
import * as productSelectCss from "../create-return/non-shopify-claim.module.css";

export const Product = memo(function Product({
  product,
  selected,
  handleSelect,
}: {
  product: NonNullable<ReturnProduct>;
  selected: boolean;
  handleSelect: (product: NonNullable<ReturnProduct>) => void;
}) {
  const { formatCurrency } = useContext(CurrencyContext);

  const handleClick = () => {
    handleSelect(product);
  };

  return (
    <div
      className={classNames(
        productSelectCss.product,
        gridCss.span3L,
        gridCss.span4M,
        gridCss.span6,
        { [productSelectCss.selected]: selected },
      )}
      onClick={handleClick}
    >
      <img className={productSelectCss.image} src={product.image} />
      <div className={productSelectCss.text}>
        <div className={productSelectCss.title}>{product.title}</div>
        <div className={productSelectCss.price}>
          {formatCurrency(product.price)}
        </div>
      </div>
    </div>
  );
});

export const filterSearchByCollection = (
  products: ProductInfo[],
  collectionId: string,
): ReturnProduct[] => {
  const filteredProducts = [] as ReturnProduct[];
  for (const product of products) {
    const returnProduct = productInfoToReturnProduct(product);
    if (
      returnProduct &&
      product.collections.nodes.some(
        (collection) => collection.id === collectionId,
      )
    ) {
      filteredProducts.push(returnProduct);
    }
  }
  return filteredProducts;
};

export const ProductSelect = memo(function ProductSelect({
  handleContinue,
  settings,
}: {
  handleContinue: (selectedProduct: ReturnProduct) => void;
  settings: ReturnAppSettings;
}) {
  const storefrontClient = new ShopifyStorefrontClient(
    settings.storeUrl,
    settings.headlessAccessToken || settings.storefrontAccessToken,
  );

  const scrolled = useScrolled(document.documentElement, 300);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [products, setProducts] = useState<ReturnProduct[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<
    ReturnProduct | undefined
  >();

  const [searchString, setSearchString] = useState<string | undefined>(
    undefined,
  );
  const debouncedSearch = useDebounce(searchString, 500);

  const collectionId = settings.warranties?.warrantyRegistrationCollection;

  const [productsLoad, loadProducts] = useTriggerLoad(async () => {
    if (!collectionId) return { hasNextPage: false };

    const res = await getCollectionProductsPaginated(storefrontClient, {
      collectionId,
      cursor,
      pageSize: 20,
    });
    setCursor(res.cursor);

    const uniqueNewProducts = res.products.filter(
      (product) => !products.some((p) => p.id === product.id),
    );
    setProducts([...products, ...uniqueNewProducts]);

    return { hasNextPage: res.hasNextPage };
  });

  const [searchedProductsLoad, searchProducts] = useTriggerLoad(async () => {
    if (!debouncedSearch || !collectionId) return;

    const response = await searchStorefrontProducts(
      storefrontClient,
      debouncedSearch,
      100,
    );

    return filterSearchByCollection(response, collectionId);
  });

  useMemo(() => {
    loadProducts();
  }, [loadProducts]);

  useMemo(() => {
    searchProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch, searchProducts]);

  const searchedProducts = searchedProductsLoad.value ?? [];
  const productsLoading = productsLoad.pending;
  const shouldLoadProducts =
    scrolled.bottom &&
    !searchString &&
    !productsLoading &&
    productsLoad.value?.hasNextPage;

  useMemo(() => {
    if (shouldLoadProducts) {
      loadProducts();
    }
  }, [shouldLoadProducts, loadProducts]);

  const searchedProductsLoading = searchedProductsLoad.pending;
  const noProductsFound =
    products.length === 0 && !productsLoading && !debouncedSearch;
  const noSearchResults =
    searchedProducts.length === 0 &&
    !searchedProductsLoading &&
    debouncedSearch;
  const noResults = noProductsFound || noSearchResults;

  return (
    <div className={returnStyles.page}>
      <div className={returnStyles.pagePadding}>
        <div className={productSelectCss.container}>
          <div className={productSelectCss.section}>
            <div className={productSelectCss.titles}>
              <div className={productSelectCss.title}>Select item</div>
              <div className={productSelectCss.subtitle}>
                Choose the item you would like to register.{" "}
              </div>
            </div>
            <div className={productSelectCss.search}>
              <TextInput
                icon={
                  searchString ? (
                    <XIcon onClick={() => setSearchString(undefined)} />
                  ) : (
                    <SearchIcon />
                  )
                }
                onChange={setSearchString}
                placeholder="Search for a product"
                value={searchString ?? ""}
              />
            </div>
            <div className={productSelectCss.products}>
              {noResults ? (
                <div style={{ width: "auto" }}>No products found.</div>
              ) : searchedProductsLoading ? (
                <LoadingRedoAnimation style={SpinnerStyle.AUTO} />
              ) : searchedProducts.length > 0 ? (
                <div className={gridCss.grid}>
                  {searchedProducts.map((product) => (
                    <Product
                      handleSelect={setSelectedProduct}
                      key={product.id}
                      product={product}
                      selected={selectedProduct?.id === product.id}
                    />
                  ))}
                </div>
              ) : (
                <div className={gridCss.grid}>
                  {products.map((product, index) => (
                    <Product
                      handleSelect={setSelectedProduct}
                      key={product.id}
                      product={product}
                      selected={selectedProduct?.id === product.id}
                    />
                  ))}
                </div>
              )}
              {productsLoading && !searchedProductsLoading && (
                <LoadingRedoAnimation style={SpinnerStyle.AUTO} />
              )}
            </div>
          </div>
          <div className={footerCss.questionPageFooter}>
            <Footer
              buttonDisabled={!selectedProduct}
              buttonPending={false}
              buttonText="Continue"
              draftReturn={undefined}
              handleClick={() =>
                selectedProduct && handleContinue(selectedProduct)
              }
              hideBranding={settings?.hidePortalBranding}
              showTooltip={false}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
